import React, { useEffect, useMemo, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Check,
  Users,
  TrendingUp,
  BarChart3,
  Building,
  Crown,
} from 'lucide-react'

type PlanId = 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE'

type PlanUsage = {
  current_member_count?: number | null
  member_limit?: number | null
  current_team_count?: number | null
  team_limit?: number | null
} | null | undefined

type PlanDefinition = {
  id: PlanId
  name: string
  price: number
  description: string
  icon: LucideIcon
  popular?: boolean
  features: string[]
  limits: {
    members: number
    teams: number
  }
  displayLimits: string
}

const plans: PlanDefinition[] = [
  {
    id: 'FREE',
    name: 'Free Plan',
    price: 0,
    description: 'Get started with basic features',
    icon: Users,
    features: [
      'Basic collaboration tools',
      'Email support',
      'Standard reporting',
    ],
    limits: {
      members: 5,
      teams: 1,
    },
    displayLimits: '5 members, 1 team',
  },
  {
    id: 'STARTER',
    name: 'Starter Plan',
    price: 29,
    description: 'Perfect for growing teams',
    icon: TrendingUp,
    popular: true,
    features: [
      'Priority email support',
      'Advanced feedback tools',
      'Team templates',
    ],
    limits: {
      members: 20,
      teams: 2,
    },
    displayLimits: '20 members, 2 teams',
  },
  {
    id: 'PROFESSIONAL',
    name: 'Professional Plan',
    price: 79,
    description: 'Advanced features for larger teams',
    icon: BarChart3,
    features: [
      'Advanced analytics',
      'Custom branding',
      'Dedicated success manager',
    ],
    limits: {
      members: 60,
      teams: 6,
    },
    displayLimits: '60 members, 6 teams',
  },
  {
    id: 'ENTERPRISE',
    name: 'Enterprise Plan',
    price: 199,
    description: 'Enterprise-grade controls and scale',
    icon: Building,
    features: [
      'Unlimited teams',
      'SOC 2 compliant security',
      'Custom contract & invoicing',
    ],
    limits: {
      members: 300,
      teams: 30,
    },
    displayLimits: '300 members, 30 teams',
  },
]

const planOrder: PlanId[] = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE']

interface PlanUpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  onUpgrade: (planId: PlanId) => Promise<void> | void
  currentPlan: PlanId
  organizationUsage?: PlanUsage
}

export function PlanUpgradeModal({
  isOpen,
  onClose,
  onUpgrade,
  currentPlan,
  organizationUsage,
}: PlanUpgradeModalProps) {
  const normalizedCurrentPlan = useMemo(() => {
    return planOrder.includes(currentPlan) ? currentPlan : 'FREE'
  }, [currentPlan])

  const defaultSelection = useMemo(() => {
    const currentIndex = planOrder.indexOf(normalizedCurrentPlan)
    return planOrder[currentIndex + 1] ?? normalizedCurrentPlan
  }, [normalizedCurrentPlan])

  const [selectedPlan, setSelectedPlan] = useState<PlanId>(defaultSelection)

  useEffect(() => {
    if (isOpen) {
      setSelectedPlan(defaultSelection)
    }
  }, [isOpen, defaultSelection])

  const isSelectable = (planId: PlanId) => {
    const planIndex = planOrder.indexOf(planId)
    const currentIndex = planOrder.indexOf(normalizedCurrentPlan)
    return planIndex > currentIndex
  }

  const handleConfirm = async () => {
    try {
      await Promise.resolve(onUpgrade(selectedPlan))
    } finally {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl font-bold">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-border">
              <Crown className="h-6 w-6 text-muted-foreground" />
            </span>
            Upgrade Your Plan
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Choose the plan that fits your team. You can manage billing through Stripe at any time.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {plans.map((plan) => {
            const Icon = plan.icon
            const isCurrent = plan.id === normalizedCurrentPlan
            const selectable = isSelectable(plan.id)
            const isSelected = plan.id === selectedPlan

            return (
              <Card
                key={plan.id}
                className={`relative transition-all ${
                  selectable
                    ? isSelected
                      ? 'border-primary shadow-lg shadow-primary/20'
                      : 'border-border hover:border-primary'
                    : 'border-dashed border-muted-foreground/40 opacity-50'
                }`}
                onClick={() => {
                  if (selectable) {
                    setSelectedPlan(plan.id)
                  }
                }}
              >
                {plan.popular && (
                  <Badge className="absolute right-4 top-4" variant="secondary">
                    Most Popular
                  </Badge>
                )}
                {isCurrent && (
                  <Badge className="absolute left-4 top-4" variant="outline">
                    Current Plan
                  </Badge>
                )}
                <CardHeader className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <CardTitle className="text-xl font-semibold">{plan.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{plan.description}</p>
                    </div>
                  </div>
                  <div className="text-3xl font-bold">
                    {plan.price === 0 ? 'Free' : `$${plan.price}`}
                    <span className="text-base font-normal text-muted-foreground"> / month</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-muted-foreground">
                    {plan.displayLimits}
                  </div>
                  <ul className="space-y-2 text-sm">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 text-primary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {!selectable && !isCurrent && (
                    <p className="text-xs text-muted-foreground">
                      Upgrade to higher tiers to unlock additional features.
                    </p>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>

        <div className="flex items-center justify-between border-t border-border pt-4">
          {organizationUsage ? (
            <div className="text-xs text-muted-foreground">
              <p>
                Current usage: {organizationUsage.current_member_count ?? 0} members /{' '}
                {organizationUsage.member_limit ?? '—'} limit, {organizationUsage.current_team_count ?? 0} teams /{' '}
                {organizationUsage.team_limit ?? '—'} limit
              </p>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">
              Usage information unavailable. You can still upgrade — we will verify limits after payment.
            </span>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedPlan === normalizedCurrentPlan}
            >
              Continue with {selectedPlan.charAt(0) + selectedPlan.slice(1).toLowerCase()} plan
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export type { PlanId }
